import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText,
  Typography, CircularProgress, Alert, Radio, RadioGroup, FormControlLabel,
  useMediaQuery, useTheme, IconButton, Divider
} from '@mui/material';
import { CreditCard, X as CloseIcon } from 'lucide-react';

const BankAccountModal = ({ 
  open, 
  onClose, 
  existingAccounts = [], 
  defaultAccount, 
  refreshData,
  getAuthToken,
  payoutService 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(existingAccounts.length > 0 ? 'select' : 'add');
  
  // Form state
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountType, setAccountType] = useState('checking');
  const [selectedAccountId, setSelectedAccountId] = useState(defaultAccount?.id || '');
  const [makeDefault, setMakeDefault] = useState(true);
  
  // Form validation
  const [errors, setErrors] = useState({});
  
  const validateForm = () => {
    const newErrors = {};
    
    if (mode === 'add') {
      if (!accountHolderName.trim()) newErrors.accountHolderName = 'Name is required';
      if (!accountNumber.trim()) newErrors.accountNumber = 'Account number is required';
      if (accountNumber.trim().length < 6) newErrors.accountNumber = 'Account number is too short';
      if (!routingNumber.trim()) newErrors.routingNumber = 'Routing number is required';
      if (routingNumber.trim().length !== 9) newErrors.routingNumber = 'Routing number must be 9 digits';
    } else if (mode === 'select' && !selectedAccountId) {
      newErrors.selectedAccountId = 'Please select an account';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleAddBankAccount = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAuthToken();
      
      await payoutService.addBankAccount({
        account_holder_name: accountHolderName,
        account_number: accountNumber,
        routing_number: routingNumber,
        account_type: accountType,
        make_default: makeDefault
      }, token);
      
      // Success - refresh data and close modal
      refreshData();
      handleClose();
    } catch (err) {
      console.error('Error adding bank account', err);
      setError(err.friendlyMessage || 'Failed to add bank account. Please check your information and try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSetDefaultAccount = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAuthToken();
      
      await payoutService.setDefaultBankAccount({
        bank_account_id: selectedAccountId
      }, token);
      
      // Success - refresh data and close modal
      refreshData();
      handleClose();
    } catch (err) {
      console.error('Error setting default bank account', err);
      setError(err.friendlyMessage || 'Failed to update default bank account. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenPayoutDashboard = () => {
    window.open('https://dashboard.stripe.com', '_blank');
  };
  
  const handleSubmit = () => {
    if (mode === 'add') {
      handleAddBankAccount();
    } else if (mode === 'select') {
      handleSetDefaultAccount();
    }
  };
  
  const handleClose = () => {
    // Reset form state
    setMode(existingAccounts.length > 0 ? 'select' : 'add');
    setAccountHolderName('');
    setAccountNumber('');
    setRoutingNumber('');
    setAccountType('checking');
    setSelectedAccountId(defaultAccount?.id || '');
    setMakeDefault(true);
    setError(null);
    setErrors({});
    
    onClose();
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={loading ? null : handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh',
          height: isMobile ? '100%' : 'auto'
        }
      }}
    >
      {/* Header - Desktop and Mobile */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          px: 2,
          py: isMobile ? 2 : 2.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        {isMobile && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
            sx={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'rgba(0, 0, 0, 0.54)'
            }}
          >
            <CloseIcon size={22} />
          </IconButton>
        )}
        
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            fontSize: '1.125rem',
            color: '#111827', // Ensuring the title is black on both mobile and desktop
            textAlign: 'center'
          }}
        >
          Manage Bank Accounts
        </Typography>
      </Box>
      
      <DialogContent 
        sx={{ 
          px: { xs: 2.5, sm: 3 }, 
          py: { xs: 3, sm: 3 },
          overflow: 'auto',
          height: isMobile ? 'calc(100% - 180px)' : 'auto'
        }}
      >
        {/* Error message */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            {error}
          </Alert>
        )}
        
        <Typography 
          fontWeight={600} 
          variant="subtitle1" 
          sx={{ 
            mb: 2.5,
            fontSize: { xs: '1rem', sm: '1.1rem' },
            color: '#111827'
          }}
        >
          Select an account for payouts
        </Typography>
        
        {existingAccounts.length > 0 && (
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            sx={{ 
              mb: 3,
              '& .MuiRadio-root': {
                padding: 1,
                color: '#2563EB' // Blue radio buttons as shown in the images
              },
              '& .MuiTypography-root': {
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                fontWeight: 500,
                color: '#111827'
              }
            }}
          >
            <FormControlLabel 
              value="select" 
              control={<Radio />} 
              label="Use existing bank account" 
            />
            <FormControlLabel 
              value="add" 
              control={<Radio />} 
              label="Add new bank account" 
            />
          </RadioGroup>
        )}
        
        {mode === 'select' && (
          <Box sx={{ mb: 3 }}>
            {existingAccounts.map((account) => (
              <Box 
                key={account.id} 
                sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: 2,
                  p: { xs: 2, sm: 2.5 }, 
                  mb: 2,
                  bgcolor: 'rgba(236, 253, 255, 0.4)', // Light blue background as shown in the images
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Radio
                    checked={selectedAccountId === account.id}
                    onChange={() => setSelectedAccountId(account.id)}
                    name="bank-account-radio"
                    sx={{ 
                      p: 1,
                      color: '#2563EB' // Blue radio buttons
                    }}
                  />
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box 
                        component="span" 
                        sx={{ 
                          color: '#FF4775', 
                          display: 'flex', 
                          alignItems: 'center',
                          mr: 1
                        }}
                      >
                        <CreditCard size={16} />
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#111827',
                          textTransform: 'uppercase'
                        }}
                      >
                        STRIPE TEST BANK •••• {account.last4}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{
                        display: 'block',
                        ml: 4,
                        fontSize: '0.75rem',
                        color: 'rgba(0, 0, 0, 0.6)'
                      }}
                    >
                      Added on {formatDate(account.created_at)} • {account.account_type || 'checking'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  mt: 1,
                  ml: 6,
                  gap: 1.5
                }}>
                  {defaultAccount && defaultAccount.id === account.id && (
                    <Box 
                      component="span" 
                      sx={{ 
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        borderRadius: 50,
                        bgcolor: 'rgba(0, 180, 216, 0.1)',
                        color: '#00B4D8',
                        fontWeight: 600
                      }}
                    >
                      Default
                    </Box>
                  )}
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      borderRadius: 50,
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10B981',
                      fontWeight: 600
                    }}
                  >
                    Linked
                  </Box>
                </Box>
              </Box>
            ))}
            
            <Typography 
              variant="body2" 
              color="textSecondary" 
              sx={{ 
                mt: 2,
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: 'rgba(0, 0, 0, 0.6)'
              }}
            >
              You can always unlink your accounts in the "Linked external accounts" section of your account settings.
            </Typography>
          </Box>
        )}
        
        {mode === 'add' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  mb: 0.75,
                  fontSize: '0.9375rem',
                  fontWeight: 500
                }}
              >
                Account Holder Name
              </Typography>
              <TextField
                fullWidth
                placeholder="Account Holder Name"
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
                error={!!errors.accountHolderName}
                helperText={errors.accountHolderName}
                disabled={loading}
                autoComplete="name"
                variant="outlined"
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                    fontSize: '1rem',
                  }
                }}
                InputProps={{
                  sx: {
                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                  }
                }}
              />
              
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  mb: 0.75,
                  fontSize: '0.9375rem',
                  fontWeight: 500
                }}
              >
                Account Number
              </Typography>
              <TextField
                fullWidth
                placeholder="Account Number"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
                disabled={loading}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                autoComplete="off"
                variant="outlined"
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                    fontSize: '1rem',
                  }
                }}
                InputProps={{
                  sx: {
                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                  }
                }}
              />
              
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  mb: 0.75,
                  fontSize: '0.9375rem',
                  fontWeight: 500
                }}
              >
                Routing Number
              </Typography>
              <TextField
                fullWidth
                placeholder="Routing Number"
                value={routingNumber}
                onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
                error={!!errors.routingNumber}
                helperText={errors.routingNumber}
                disabled={loading}
                inputProps={{ maxLength: 9, inputMode: 'numeric', pattern: '[0-9]*' }}
                autoComplete="off"
                variant="outlined"
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                    fontSize: '1rem',
                  }
                }}
                InputProps={{
                  sx: {
                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                  }
                }}
              />
              
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  mb: 0.75,
                  fontSize: '0.9375rem',
                  fontWeight: 500
                }}
              >
                Account Type
              </Typography>
              <Select
                fullWidth
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                disabled={loading}
                sx={{ 
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                  '& .MuiSelect-select': {
                    padding: '14px 16px',
                    fontSize: '1rem',
                  },
                  bgcolor: 'rgba(0, 0, 0, 0.02)'
                }}
              >
                <MenuItem value="checking">Checking</MenuItem>
                <MenuItem value="savings">Savings</MenuItem>
              </Select>
              <Typography 
                variant="caption" 
                color="textSecondary" 
                sx={{ 
                  mb: 3, 
                  display: 'block',
                  fontSize: '0.75rem'
                }}
              >
                Select the type of your bank account
              </Typography>
              
              {existingAccounts.length > 0 && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mt: 2,
                    py: 0.5
                  }}
                >
                  <Radio
                    checked={makeDefault}
                    onChange={(e) => setMakeDefault(e.target.checked)}
                    disabled={loading}
                    sx={{ 
                      p: 1,
                      color: '#2563EB' // Blue radio buttons
                    }}
                  />
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: '0.9375rem' }}
                  >
                    Set as default payment method
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      
      {!isMobile && <Divider />}
      
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: 'transparent'
        }}
      >
        <Typography 
          variant="body2" 
          color="textSecondary"
          sx={{ 
            fontSize: '0.875rem',
            lineHeight: 1.5
          }}
        >
          By adding a bank account, you agree to the Stripe Connect{' '}
          <Box 
            component="a" 
            href="https://stripe.com/connect-account/legal" 
            target="_blank" 
            rel="noopener noreferrer" 
            sx={{ 
              color: '#FF4775', 
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            Account Agreement
          </Box>
          .
        </Typography>
      </Box>
      
      {!isMobile && <Divider />}
      
      {/* Different button layouts for desktop and mobile */}
      {isMobile ? (
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || (mode === 'select' && selectedAccountId === defaultAccount?.id)}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#FF4775',
              fontSize: '1rem',
              py: 1.5,
              borderRadius: 50,
              '&:hover': {
                bgcolor: '#E63E69'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255, 71, 117, 0.5)'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : mode === 'add' ? (
              'Add Account'
            ) : (
              'Set Default'
            )}
          </Button>
          
          <Button 
            onClick={handleClose}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#6B7280',
              fontSize: '1rem',
              py: 1.5,
              borderRadius: 50,
              bgcolor: 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.09)'
              }
            }}
          >
            Cancel
          </Button>
          
          <Button 
            onClick={handleOpenPayoutDashboard}
            fullWidth
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#6366F1',
              fontSize: '1rem',
              py: 1.5,
              borderRadius: 50,
              borderColor: 'rgba(99, 102, 241, 0.3)',
              '&:hover': {
                borderColor: 'rgba(99, 102, 241, 0.8)',
                bgcolor: 'rgba(99, 102, 241, 0.04)'
              }
            }}
          >
            Stripe Dashboard
          </Button>
        </Box>
      ) : (
        <Box 
          sx={{ 
            px: 3, 
            py: 2.5, 
            display: 'flex', 
            justifyContent: 'space-between' 
          }}
        >
          <Button 
            onClick={handleOpenPayoutDashboard}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#6366F1',
              borderColor: 'rgba(99, 102, 241, 0.3)',
              borderRadius: 50,
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: 'rgba(99, 102, 241, 0.8)',
                bgcolor: 'rgba(99, 102, 241, 0.04)'
              }
            }}
          >
            Stripe Dashboard
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#6B7280',
                bgcolor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: 50,
                px: 3,
                py: 1,
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.09)'
                }
              }}
            >
              Cancel
            </Button>
            
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading || (mode === 'select' && selectedAccountId === defaultAccount?.id)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#FF4775',
                borderRadius: 50,
                px: 3,
                py: 1,
                '&:hover': {
                  bgcolor: '#E63E69'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 71, 117, 0.5)'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : mode === 'add' ? (
                'Add Account'
              ) : (
                'Set Default'
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default BankAccountModal;